import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Award, Camera, ChevronLeft, ChevronRight } from "lucide-react";
import { DIRECTOR, TRAINERS } from "../lib/site";

function DirectorSlideshow({ images, name }) {
  const [idx, setIdx] = useState(0);
  const total = images.length;

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(t);
  }, [total]);

  return (
    <div className="relative aspect-[4/5] overflow-hidden bg-secondary grain group" data-testid="director-slideshow">
      <AnimatePresence mode="wait">
        <motion.img
          key={idx}
          src={images[idx]}
          alt={name}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {/* Director badge */}
      <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] z-10">
        Director
      </div>
      <div className="absolute bottom-4 right-4 bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] z-10">
        #BeGreat
      </div>

      {/* Controls */}
      <button
        type="button"
        onClick={() => setIdx((i) => (i - 1 + total) % total)}
        aria-label="Previous"
        data-testid="director-prev"
        className="absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm p-2 hover:bg-primary hover:text-primary-foreground transition-colors text-foreground opacity-0 group-hover:opacity-100 z-10"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        type="button"
        onClick={() => setIdx((i) => (i + 1) % total)}
        aria-label="Next"
        data-testid="director-next"
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm p-2 hover:bg-primary hover:text-primary-foreground transition-colors text-foreground opacity-0 group-hover:opacity-100 z-10"
      >
        <ChevronRight size={18} />
      </button>

      {/* Indicator dots */}
      <div className="absolute bottom-4 left-4 flex gap-1.5 z-10">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            data-testid={`director-dot-${i}`}
            className={`h-1 transition-all ${i === idx ? "w-8 bg-primary" : "w-4 bg-background/60"}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Trainers() {
  const team = TRAINERS.filter((t) => t.id !== DIRECTOR.id);
  const directorImages = DIRECTOR.images && DIRECTOR.images.length > 0 ? DIRECTOR.images : [DIRECTOR.image];

  return (
    <section id="trainers" data-testid="trainers-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
              Coached by <br />
              <span className="text-primary">proven developers</span> <br />
              and teachers.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-foreground/70 text-base md:text-lg max-w-md">
              A staff of elite players and proven developers — every coach has lived the game at the highest levels and
              now pours that experience into the next generation.
            </p>
          </div>
        </div>

        {/* DIRECTOR — Isaac */}
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-24 md:mb-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-6"
            data-testid={`trainer-card-${DIRECTOR.id}`}
          >
            <DirectorSlideshow images={directorImages} name={DIRECTOR.name} />
            <div className="mt-5 grid grid-cols-2 gap-3">
              {DIRECTOR.stats.map((s) => (
                <div key={s.label} className="border-l-2 border-primary pl-3 py-1">
                  <div className="font-display text-2xl md:text-3xl leading-none">{s.value}</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mt-1 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 md:col-span-6 md:pt-2"
          >
            <h3 className="font-display uppercase text-5xl md:text-7xl leading-[0.85]">
              {DIRECTOR.name.split(" ")[0]} <br />
              <span className="text-primary">{DIRECTOR.name.split(" ").slice(1).join(" ")}</span>
            </h3>
            <div className="mt-3 text-xs font-bold uppercase tracking-[0.3em] text-foreground/70">{DIRECTOR.role}</div>
            <div className="mt-8 space-y-5 text-foreground/80 text-base md:text-lg leading-relaxed">
              <p>{DIRECTOR.bio}</p>
              <p>{DIRECTOR.bioExtended}</p>
            </div>
            <div className="mt-10">
              <div className="flex items-center gap-3 mb-4">
                <Award size={16} className="text-primary" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">Honors & Accolades</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {DIRECTOR.accolades.map((a) => (
                  <li key={a} className="text-sm font-medium flex items-start gap-2">
                    <span className="text-primary mt-1">▸</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <a href="#contact" data-testid="trainer-cta" className="mt-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] border-b-2 border-foreground pb-1 hover:text-primary hover:border-primary transition-colors" style={{ display: "none" }}>
              Train With Isaac <ArrowUpRight size={14} />
            </a>
          </motion.div>
        </div>

        {/* TEAM GRID */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14 pt-10 border-t border-foreground/15">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-primary" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">The Team</span>
            </div>
            <h3 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight">
              Built by <span className="text-primary">winners.</span>
            </h3>
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground">
            BeGreat Coaches
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {team.map((t, i) => (
            <motion.article
              key={t.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`trainer-card-${t.id}`}
              className="group"
            >
              <div className="relative aspect-[4/5] overflow-hidden mb-5 bg-secondary">
                {t.image ? (
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-full h-full object-cover object-center grayscale-[0.1] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                ) : (
                  <div className="w-full h-full bg-foreground text-background flex flex-col items-center justify-center gap-3 p-6 text-center">
                    <Camera size={36} className="text-primary" />
                    <div className="font-display text-3xl uppercase leading-none">{t.name}</div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-background/60">Capturing the story</div>
                  </div>
                )}
                <div className="absolute bottom-4 right-4 bg-primary text-primary-foreground px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em]">
                  {t.role === "Content Creator" ? "Lens" : "#BeGreat"}
                </div>
              </div>
              <h3 className="font-display uppercase text-3xl md:text-4xl leading-none">{t.name}</h3>
              <div className="mt-2 text-xs font-bold uppercase tracking-[0.25em] text-primary">{t.role}</div>
              <p className="mt-4 text-sm text-foreground/75 leading-relaxed">{t.bio}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
